import { formatISO } from "date-fns";
import { casesActions } from "configs";
import { generateId, useCurrentCase, useDispatch, useParams } from "duck";
import {
  useCreateDentalSketchCommentMutation,
  useDeleteDentalSketchCommentsMutation,
  useUpdateDentalSketchCommentMutation
} from "requests";
import { CommentLocation } from "../../../duck";
import { FormValues, Author } from "./types";

export const useCreateDentalSketchComment = (
  location: CommentLocation,
  author: Author
) => {
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const [create, { loading }] = useCreateDentalSketchCommentMutation();
  const { shouldSync, patientId } = useCurrentCase(medicalCase => ({
    shouldSync: medicalCase.shouldSync,
    patientId: medicalCase.data.client.patient._id
  }));

  return {
    create: ({ text }: FormValues) => {
      const variables = {
        locationX: location.x,
        locationY: location.y,
        text
      };

      if (shouldSync) {
        return create({
          variables: {
            input: {
              patient: patientId,
              ...variables
            }
          }
        }).then(response => {
          const dentalSketchComment =
            response.data?.createDentalSketchComment?.dentalSketchComment;
          if (dentalSketchComment) {
            dispatch(
              casesActions.addDentalSketchComment({
                data: dentalSketchComment,
                _id: caseId
              })
            );
          }
        });
      }

      dispatch(
        casesActions.addDentalSketchComment({
          _id: caseId,
          data: {
            fakeId: generateId(),
            createdAt: formatISO(new Date()),
            createdBy: author,
            ...variables
          }
        })
      );
    },
    loading
  };
};

export const useUpdateDentalSketchComment = (commentId?: string | number) => {
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const [update, { loading }] = useUpdateDentalSketchCommentMutation();
  const shouldSync = useCurrentCase(medicalCase => medicalCase.shouldSync);

  return {
    update: ({ text }: FormValues) => {
      if (!commentId) {
        return;
      }

      if (shouldSync) {
        return update({
          variables: {
            input: {
              _id: commentId as number,
              text
            }
          }
        }).then(response => {
          const dentalSketchComment =
            response.data?.updateDentalSketchComment?.dentalSketchComment;
          if (dentalSketchComment) {
            dispatch(
              casesActions.updateDentalSketchComment({
                data: dentalSketchComment,
                _id: caseId
              })
            );
          }
        });
      }

      dispatch(
        casesActions.updateDentalSketchComment({
          _id: caseId,
          data: {
            ...(typeof commentId === "string"
              ? { fakeId: commentId }
              : { _id: commentId }),
            text
          }
        })
      );
    },
    loading
  };
};

export const useDeleteDentalSketchComment = () => {
  const { id: caseId } = useParams();
  const dispatch = useDispatch();
  const [deleteComment, { loading }] = useDeleteDentalSketchCommentsMutation();
  const shouldSync = useCurrentCase(medicalCase => medicalCase.shouldSync);

  return {
    deleteComment: (commentId: string | number) => {
      if (shouldSync) {
        return deleteComment({
          variables: {
            input: {
              ids: [commentId as number]
            }
          }
        }).then(() => {
          dispatch(
            casesActions.deleteDentalSketchComments({
              data: [commentId],
              _id: caseId
            })
          );
        });
      }

      dispatch(
        casesActions.deleteDentalSketchComments({
          data: [commentId],
          _id: caseId
        })
      );
    },
    loading
  };
};
